<template>
  <div>
    <div class="vx-row justify-end">
      <div class="vx-row justify-end">
        <div class="vx-col">
          <sales-filter
            v-model="salesAsinTitle"
            class="w-80"
            :options="asinTitleOptions"
            :no-option-text="adSalesByAsinTitleFilterNoOptionText"
            placeholder="商品名"
            :multiple="false"
            close-on-select
          />
        </div>
        <div class="vx-col">
          <DateRanges
            :start-day="startDay"
            :end-day="endDay"
            :max-range="93"
            @on-change="onDateChange"
          />
        </div>
      </div>
    </div>
    <div class="mt-8">
      <vx-card class="no-shadow">
        <div class="vx-row">
          <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-1/5 xl:w-1/5">
            <div class="min-h-full flex justify-center items-center">
              <img
                class="responsive"
                :src="reportData.product.smallImage"
                :alt="reportData.product.title"
                style="width: 100px;"
              />
            </div>
          </div>
          <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-4/5 xl:w-4/5">
            <div class="knowledge-panel__meta">
              <h3>{{ reportData.product.title }}</h3>
              <div class="item-heading mb-4">
                <p class="text-lg">
                  <span>{{ reportData.product.brand }}</span>
                </p>
              </div>
              <vs-divider />
              <div class="flex mb-4">
                <vs-chip color="primary">
                  ASIN: {{ reportData.product.asin }}
                </vs-chip>
                <vs-chip>親ASIN: {{ reportData.product.parentAsin }}</vs-chip>
              </div>
              <div>
                <p><span class="font-medium mr-2">カテゴリー:</span> {{ `${reportData.product.categoryName} ${reportData.product.categorySr}位` }} </p>
                <p><span class="font-medium mr-2">サブカテゴリー:</span> {{ `${reportData.product.subcategoryName} ${reportData.product.subcategorySr}位` }} </p>
                <p><span class="font-medium mr-2">商品原価:</span> {{ `\xA5${reportData.product.cost.toLocaleString()}` }}</p>
              </div>
            </div>
          </div>
        </div>
      </vx-card>
    </div>
    <div class="mt-8">
      <vx-card class="no-shadow">
        <analytics-chart
          v-if="reportData.analytics"
          :dataset="reportData.analytics"
          :selected-left-fields="selectedLeftFields"
          :selected-right-fields="selectedRightFields"
          :height="400"
        />
      </vx-card>
    </div>
    <div class="mt-8">
      <vx-card class="no-shadow pb-10">
        <div class="grid grid-cols-3 gap-6">
          <div>
            <div class="font-bold mb-3">
              指標
            </div>
            <draggable
              :list="availableFields"
              item-key="label"
              group="analyticsFields"
              class="min-h-full border-dashed border p-2"
            >
              <template #item="{ element }">
                <vs-chip class="cursor-move">
                  <vs-avatar
                    :color="element.backgroundColor"
                    :icon="getFieldIcon(element)"
                    class="cursor-move"
                  />
                  {{ element.label }}
                </vs-chip>
              </template>
            </draggable>
          </div>
          <div>
            <div class="font-bold mb-3">
              左軸
            </div>
            <draggable
              :list="selectedLeftFields"
              item-key="label"
              group="analyticsFields"
              class="min-h-full border-dashed border p-2"
            >
              <template #item="{ element }">
                <vs-chip class="cursor-move">
                  <vs-avatar
                    :color="element.backgroundColor"
                    :icon="getFieldIcon(element)"
                    class="cursor-move"
                  />
                  {{ element.label }}
                </vs-chip>
              </template>
            </draggable>
          </div>
          <div>
            <div class="font-bold mb-3">
              右軸
            </div>
            <draggable
              :list="selectedRightFields"
              item-key="label"
              group="analyticsFields"
              class="min-h-full border-dashed border p-2"
            >
              <template #item="{ element }">
                <vs-chip class="cursor-move">
                  <vs-avatar
                    :color="element.backgroundColor"
                    :icon="getFieldIcon(element)"
                    class="cursor-move"
                  />
                  {{ element.label }}
                </vs-chip>
              </template>
            </draggable>
          </div>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import SalesFilter from '@/components/SalesFilter'
import DateRanges from '@/components/DateRanges'
import AnalyticsChart, { availableFields } from '@/user/components/charts/AnalyticsChart'
import { debounce } from '@/utils'

const MAX_ASIN_TITLE_LENGTH = 30

export default {
  name: 'Analytics',
  components: {
    SalesFilter,
    DateRanges,
    AnalyticsChart,
    draggable
  },
  data () {
    return {
      salesAsinTitle: null,
      selectedLeftFields: [],
      selectedRightFields: [],
      availableFields: []
    }
  },
  computed: {
    adSalesByAsinTitleFilterNoOptionText () {
      return this.requestingStatus.adSalesByAsinTitle ? 'Loading...' : '候補がありません'
    },
    asinTitleOptions () {
      if (!this.reportData) {
        return []
      }
      return this.reportData.adSalesByAsinTitle.map(item => {
        let text = `${item.asin} ${item.title}`
        if (text.length > MAX_ASIN_TITLE_LENGTH) {
          text = `${text.substring(0, MAX_ASIN_TITLE_LENGTH)}...`
        }
        return {
          value: item.asin,
          sales: item.adSales,
          text
        }
      })
    },
    ...mapState(['startDay', 'endDay']),
    ...mapState('analytics', [
      'requestingStatus',
      'reportData',
      'fieldKeywordMap'
    ]),
    ...mapState('account', ['activeAccount'])
  },
  watch: {
    salesAsinTitle: {
      handler () {
        this.filterReport()
      }
    },
    activeAccount: {
      handler () {
        this.resetFilters()
      }
    },
    fieldKeywordMap: {
      deep: true,
      handler (value) {
        this.availableFields = [
          ...this.availableFields.filter(field => !field.value.startsWith('keyword')),
          ...Object.entries(value).map(([key, value], i) => ({
            type: 'line',
            label: `検索順位: ${value}`,
            fill: false,
            value: key,
            backgroundColor: this.availableFields[i].backgroundColor
          }))
        ]
      }
    }
  },
  created () {
    // globalでデフォルトで値が設定されていてDateRangesで使われてるようなので、ここでDashboard用にデフォルトは「14日前から昨日」にする
    const twoWeeksAgo = moment().subtract(14, 'days')
    const yesterday = moment().subtract(1, 'days')
    this.$store.commit('SET_STARTDAY', twoWeeksAgo.format())
    this.$store.commit('SET_ENDDAY', yesterday.format())

    this.getReport()
    this.selectedLeftFields = availableFields.filter(field => field.value === 'naturalSales')
    this.selectedRightFields = availableFields.filter(field => field.value === 'subcategorySr')
    this.availableFields = availableFields.filter(field => !['subcategorySr', 'naturalSales'].includes(field.value))
  },
  methods: {
    ...mapActions('analytics', ['fetchData']),
    getFieldIcon (field) {
      return field.type === 'line' ? 'show_chart' : 'bar_chart'
    },
    resetFilters () {
      this.salesAsinTitle = null
    },
    onDateChange (date) {
      this.$store.commit('SET_STARTDAY', moment(date.start).format())
      this.$store.commit('SET_ENDDAY',  moment(date.end).format())
      this.getReport()
    },
    filterReport: debounce(function () {
      if (this.salesAsinTitle) {
        this.getReport(['analytics', 'product'])
      }
    }, 1000),
    async getReport (actions = []) {
      let apiActions = []
      if (actions.length > 0) {
        apiActions = actions
      } else {
        let defaultActions = ['adSalesByAsinTitle']
        if (this.salesAsinTitle) {
          defaultActions = [
            ...defaultActions,
            'analytics',
            'product'
          ]
        }
        apiActions = defaultActions
      }

      const dateParams = {
        start_day: this.startDay.split('T')[0],
        end_day: this.endDay.split('T')[0]
      }

      await Promise.all(apiActions.map(action => {
        return new Promise(async (resolve) => {
          let payload = {}
          switch (action) {
            case 'analytics':
              payload = {
                ...dateParams,
                asin: this.salesAsinTitle.value,
                action
              }
              break
            case 'product':
              payload = {
                asin: this.salesAsinTitle.value,
                action
              }
              break
            case 'adSalesByAsinTitle':
              payload = {
                ...dateParams,
                action
              }
              break
            default:
              throw new Error('action not found')
          }
          try {
            const result = await this.fetchData(payload)
            if (action === 'adSalesByAsinTitle' && this.salesAsinTitle === null && this.reportData.adSalesByAsinTitle.length > 0) {
              this.salesAsinTitle = this.asinTitleOptions[0]
            }
            resolve(result)
          } catch (e) {
            console.log(e)
          }
        })
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.min-h-full.border-dashed.border {
  border-color: #a0aec0;
}
</style>
